import type {
  Order,
  OrderBillingAddressRequest,
  OrderCheckoutRequest,
  OrderCheckoutResponse,
  OrderDetail,
  OrderListReponse,
  OrderShippingAddressRequest,
  OrderUpdateAddressResponse,
} from "@features/shopping/types";

import { orderService } from "@features/shopping/api";
import { defineStore } from "pinia";

type OrderState = {
  error: null | string;
  loading: boolean;
  orders: null | Order[];
};

export const useOrderStore = defineStore("order", {
  actions: {
    async cancelOrder(memberId: string, orderId: string) {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.cancelOrder(orderId);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        this.fetchOrders(memberId);

        return response;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : "An error occurred during order cancellation";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    clearError() {
      this.error = null;
    },

    clearOrder() {
      this.orders = null;
    },

    async fetchOrderDetails(orderId: string): Promise<null | OrderDetail> {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.getOrderDetails(orderId);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        return response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "Failed to fetch order details";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchOrders(memberId: string): Promise<null | OrderListReponse> {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.getOrderList(memberId);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        this.orders = response.orders;

        return response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "Failed to fetch order list";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async initializeOrders(memberId: string) {
      await this.fetchOrders(memberId);
    },

    async orderCheckout(orderData: OrderCheckoutRequest): Promise<null | OrderCheckoutResponse> {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.checkoutOrder(orderData);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        this.fetchOrders(orderData.member_id);

        return response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "An error occurred during checkout";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateOrderBillingAddress(
      data: OrderBillingAddressRequest,
    ): Promise<null | OrderUpdateAddressResponse> {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.updateOrderBillingAddress(data);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        return response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "Failed to update order billing address";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateOrderShippingAddress(
      data: OrderShippingAddressRequest,
    ): Promise<null | OrderUpdateAddressResponse> {
      this.loading = true;
      this.error = null;

      try {
        const response = await orderService.updateOrderShippingAddress(data);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        return response;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "Failed to update order shipping address";
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },

  getters: {
    getOrders: (state) => state.orders,
    hasOrders: (state) => state.orders && state.orders.length > 0,
  },

  state: (): OrderState => ({
    error: null,
    loading: false,
    orders: null,
  }),
});
